import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { DriverInfo, FrontCard } from '../../components/FrontCard/FrontCard';
import Layout, { OrderRequestResult } from '../../components/Layout/Layout';
import { useHistory } from 'react-router';
import { Routes } from '../../routes/types';
import { useGetDriverLocationQuery } from '../../apis/mlkApi/mlk.api.hooks';
import {
  extractLatestServiceAttemptIfExist,
  getCorrectStatus,
  getDriverInfo,
  getExpectedServiceDateAndTimeSlot,
  getFunnel,
  getProblem,
  isDriverLocationNecessary,
} from '../../helpers/order';
import { SignUpModal } from '../../components/SignUpModal/SignUpModal';
import { BlockedReason, CalculatedOrderStatus, Problem } from '../../models';
import { useMlkActions } from '../../hooks/mlkActions/useMlkActions';
import { GaEvent, pushEvent } from '../../helpers/googleAnalytics';
import { useQueryParams } from '../../hooks/useQueryParams';

type HomeViewProps = {};

export const HomeView: React.FC<HomeViewProps> = () => {
  const queryParams = useQueryParams();
  const orderIdFromQp = queryParams.get('id');
  const filter = queryParams.get('filter');

  const history = useHistory();
  const actionsAvailable = useMlkActions();

  const [isDriverArriving, setIsDriverArriving] = useState(false);
  const [{ data: order }, setOrderRequestData] = useState<OrderRequestResult>({
    isLoading: true,
    isFetching: false,
  });

  const address = order?.deliveryAddress;
  const latestAttempt = useMemo(
    () => extractLatestServiceAttemptIfExist(order),
    [order]
  );

  const latestAttemptDateTime = latestAttempt && dayjs(latestAttempt.timestamp);
  const headerLogoAlt = order?.shipper.alias || order?.shipper.name;
  const headerLogoSrc = order?.shipper.logo;
  const attemptNumber = order?.attempt;
  const problem = getProblem(order);
  const pinCode = order?.securityPin;
  const comment = order?.review?.comment;
  const rating = order?.review?.rating;
  const delay = order?.delay;
  const territoryId = order?.shipperTerritoryConfiguration.territoryId;
  const expectedServiceDateAndTimeSlot = useMemo(
    () => getExpectedServiceDateAndTimeSlot(order),
    [order]
  );

  const calculatedOrderStatus = useMemo(
    () => order && getCorrectStatus(order),
    [order]
  );

  console.log('Calculated order status', calculatedOrderStatus);

  const funnel = useMemo(() => getFunnel(order), [order]);

  const { data: updatedDriverLocation, error: driverLocationError } =
    useGetDriverLocationQuery(order?.opaqueId || '', {
      skip: isDriverLocationNecessary(
        order,
        isDriverArriving,
        calculatedOrderStatus
      ),
      pollingInterval: 30 * 1000,
    });

  const driverInfo: DriverInfo | undefined = useMemo(() => {
    return getDriverInfo(order, calculatedOrderStatus, updatedDriverLocation);
  }, [calculatedOrderStatus, order, updatedDriverLocation]);

  const onDetailsClick = useCallback(() => {
    const orderIdParam = orderIdFromQp ? `?id=${orderIdFromQp}` : '';
    const filterIdParam = filter === 'noassign' ? `&filter=noassign` : '';
    const pathToPush = `${Routes.Details}${orderIdParam ? orderIdParam : ''}${
      filterIdParam ? filterIdParam : ''
    }`;
    history.push(pathToPush);
  }, [filter, history, orderIdFromQp]);

  const dateTime = useMemo(() => {
    if (
      calculatedOrderStatus === CalculatedOrderStatus.Scheduled ||
      calculatedOrderStatus === CalculatedOrderStatus.InTransit
    ) {
      return expectedServiceDateAndTimeSlot?.from || funnel?.from;
    } else {
      return latestAttemptDateTime;
    }
  }, [
    calculatedOrderStatus,
    expectedServiceDateAndTimeSlot?.from,
    funnel?.from,
    latestAttemptDateTime,
  ]);

  const arrivingDate = useMemo(() => {
    if (
      calculatedOrderStatus === CalculatedOrderStatus.Scheduled ||
      calculatedOrderStatus === CalculatedOrderStatus.InTransit
    ) {
      return expectedServiceDateAndTimeSlot?.to || funnel?.to;
    } else {
      return undefined;
    }
  }, [calculatedOrderStatus, expectedServiceDateAndTimeSlot?.to, funnel?.to]);

  const noteForCustomer = useMemo(() => {
    if (calculatedOrderStatus === CalculatedOrderStatus.Fulfilled) {
      if (order?.serviceAttempts && order.serviceAttempts.length > 0) {
        return order.serviceAttempts[0].noteForCustomer;
      }
    }
    return undefined;
  }, [calculatedOrderStatus, order?.serviceAttempts]);

  const poInfo = useMemo(() => {
    if (
      order &&
      order.blockedReasons?.includes(BlockedReason.B01) &&
      (problem === Problem.F01 || problem === Problem.F02) &&
      order.fallbackParameters &&
      order.fallbackParameters.fallbackFirstAvailabilityInPostalOffice
    ) {
      const dateRange = dayjs(
        order.fallbackParameters.fallbackFirstAvailabilityInPostalOffice
      );
      return {
        poName: order.fallbackParameters.fallbackAddress,
        poFromDate: dateRange,
      };
    }
  }, [order, problem]);

  const isLastAttempt = Boolean(
    order?.blockedReasons?.includes(BlockedReason.B01)
  );

  const goToHelp = useCallback(() => {
    if (order) {
      history.push(Routes.Help.replace(':mlkId', order.opaqueId));
    } else {
      history.push(Routes.HelpSearchOrder);
    }
  }, [history, order]);

  const setOrder = useCallback((orderResult: OrderRequestResult) => {
    if (orderResult.data) {
      const calculatedOrderStatus = getCorrectStatus(orderResult.data);
      pushEvent(GaEvent.OrderOpened, {
        name: GaEvent.OrderOpened,
        shipper_order_code: orderResult.data.shipperOrderCode,
        milkman_order_state: orderResult.data.status,
        order_id: orderResult.data.id,
        shipper_id: orderResult.data.shipper.id,
        shipper_name: orderResult.data.shipper.name,
        order_state_calculated: calculatedOrderStatus!,
        territory_id:
          +orderResult.data.shipperTerritoryConfiguration.territoryId,
      });
    }
    setOrderRequestData(orderResult);
  }, []);

  useEffect(() => {
    if (order?.showMap && !!order.driverLocation) {
      setIsDriverArriving(true);
    }
  }, [order?.driverLocation, order?.showMap]);

  useEffect(() => {
    if (driverLocationError) {
      setIsDriverArriving(false);
    }
  }, [driverLocationError]);

  return (
    <Layout
      hasHelp
      isHome
      isGreyBg
      onHelpClick={goToHelp}
      onOrderChange={setOrder}
    >
      {order ? (
        <FrontCard
          isLastAttempt={isLastAttempt}
          poName={poInfo?.poName}
          poFromDate={poInfo?.poFromDate}
          noteForCustomer={noteForCustomer}
          cashOnDeliveryAmount={order.amount}
          serviceDeadline={order.serviceDeadline}
          driverInfo={driverInfo}
          address={address!}
          dateTime={dateTime}
          headerLogoAlt={headerLogoAlt!}
          headerLogoSrc={headerLogoSrc!}
          attempt={attemptNumber}
          orderState={calculatedOrderStatus!}
          problem={problem}
          onDetailsClick={onDetailsClick}
          pinCode={pinCode}
          review={comment}
          shippedProductPaymentMethod={order.paymentMethod}
          vote={rating}
          delay={delay}
          arrivingDate={arrivingDate}
          funnel={funnel}
          isNoAssign={filter === 'noassign'}
          availableActions={actionsAvailable}
          territoryId={territoryId ? parseInt(territoryId) : undefined}
        />
      ) : null}
      <SignUpModal />
      {/* {order && (
        <ConvergenceModal
          lat={order!.deliveryAddress.lat}
          lon={order!.deliveryAddress.lon}
        />
      )} */}
    </Layout>
  );
};
